
import { runPreCartCheck, estimatePrice, formatMoney, CartExtras, CartWarning } from '../lib/preCartCheck';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { LabelConfig, CustomBackground, CustomShape, CustomTemplate } from '../types';
import { 
    generateLabelSvg,
    calculateDynamicLayout,
    LayoutItem,
    A4_WIDTH, 
    A4_HEIGHT, 
    MARGIN, 
    createStickerFile,
    generateCode128Barcode,
    getBarcodeWidth,
    generateQrCodeSvg,
    getSvgInfo
} from '../lib/svgGenerator';
import { sendOrderToDrive } from '../lib/driveIntegration';
import { OrderUploadError, ORDER_MESSAGES } from '../lib/orderErrors';
import { getAppCheckHeader } from '../lib/firebase';
import { useFontsVersion } from '../lib/fontsVersion';
import { statsService } from '../lib/stats';
import StepperInput from './StepperInput';
import ReviewBadge from './ReviewBadge';
import { getLang } from '../lib/i18n';
import UsagePhotos from './UsagePhotos';

interface PreviewPanelProps {
  config: LabelConfig;
  quantity: number;
  isMobile?: boolean;
  returnUrl?: string | null;
  orderToken?: string | null;
  onFinalUrlGenerated?: (url: string) => void;
  onQuantityChange?: (newQuantity: number) => void;
  setAlertModal?: (message: string | null) => void;
  driveFolderId?: string;
  isAdminMode?: boolean;
  customBackgrounds?: CustomBackground[];
  customTemplates?: CustomTemplate[];
  customShapes?: CustomShape[];
  appLogo?: string;
  onRestartWizard?: () => void;
  sheetWidth?: number;
  sheetHeight?: number;
  bleedMm?: number;
  cutLinesEnabled?: boolean;
  compact?: boolean; // redesign: the sheet is a small secondary preview that opens full size on demand
  cartExtras?: CartExtras; // kontrola před košíkem a orientační cena (z administrace)
  showUsagePhotos?: boolean; // ukázky použití – samolepky zákazníka ve fotkách předmětů
  knownIconIds?: ReadonlySet<string>; // ids of the shop's icon library, so session-only icons are not counted one by one in the statistics
}

const StickerSheetSvg: React.FC<{
    config: LabelConfig;
    pageLayout: LayoutItem[];
    token?: string | null;
    showCutLine?: boolean;
    customBackgrounds?: CustomBackground[];
    customTemplates?: CustomTemplate[];
    customShapes?: CustomShape[];
    pageLabel?: string;
    logoUrl?: string;
    showCodes?: boolean;
    sheetWidth?: number;
    sheetHeight?: number;
    bleedMm?: number;
}> = ({ config, pageLayout, token, showCutLine, customBackgrounds, customTemplates, customShapes, pageLabel, logoUrl, showCodes, sheetWidth = A4_WIDTH, sheetHeight = A4_HEIGHT, bleedMm = 1 }) => {

    const [qrSvg, setQrSvg] = useState<string>('');
    useFontsVersion(); // lay the labels out again once web fonts are loaded (text is measured with the real font)

    const labels: React.ReactNode[] = pageLayout.map((item, idx) => {
        const labelSvg = generateLabelSvg(config, item.variant, item.x, item.y, item.w, item.h, showCutLine, customBackgrounds, customShapes, undefined, false, customTemplates, bleedMm);
        return <g key={`${idx}`} dangerouslySetInnerHTML={{ __html: labelSvg }} />;
    });

    const LOGO_H = 7;
    const LOGO_W = 32;
    const FOOTER_Y = sheetHeight - LOGO_H - 1;
    const TEXT_Y = FOOTER_Y + LOGO_H - 1;

    const shouldShowCodes = token && showCodes;

    const barcodeWidth = shouldShowCodes ? getBarcodeWidth(token) : 0;
    const barcodeX = sheetWidth - 10 - barcodeWidth;
    const barcodeSvg = shouldShowCodes ? generateCode128Barcode(token, barcodeX, TEXT_Y - 7, 7) : '';

    useEffect(() => {
        if (shouldShowCodes) {
            const qrSize = 7;
            const qrX = barcodeX - qrSize - 3;
            generateQrCodeSvg(token, qrX, TEXT_Y - qrSize, qrSize).then(setQrSvg);
        } else {
            setQrSvg('');
        }
    }, [shouldShowCodes, barcodeX, token, TEXT_Y]);

    return (
        <div className="rd-sheet relative w-full flex justify-center group">
            <svg
                className="rd-sheet-svg w-full h-auto bg-white"
                viewBox={`0 0 ${sheetWidth} ${sheetHeight}`}
                style={{ aspectRatio: `${sheetWidth}/${sheetHeight}` }}
                aria-label={pageLabel || "Náhled archu"}
            >
              {showCutLine && (
                <rect
                    x="0"
                    y="0"
                    width={sheetWidth}
                    height={sheetHeight}
                    fill="none"
                    stroke="#FF00FF"
                    strokeWidth="0.5"
                    pointerEvents="none"
                />
              )}

              {labels}
              
              {logoUrl && (
                 <g>
                    <image
                        x={MARGIN}
                        y={FOOTER_Y}
                        width={LOGO_W}
                        height={LOGO_H}
                        href={logoUrl}
                        preserveAspectRatio="xMinYMid meet"
                    />
                 </g>
              )}

              {shouldShowCodes && (
                 <g>
                     <g dangerouslySetInnerHTML={{ __html: barcodeSvg }} />
                     <g dangerouslySetInnerHTML={{ __html: qrSvg }} />
                     <text
                        x={MARGIN + 35}
                        y={TEXT_Y}
                        textAnchor="start"
                        fontFamily="Arial, sans-serif" 
                        fontSize="2.5" 
                        fill="#000000"
                        fontWeight="bold"
                     >
                        ID: {token} {pageLabel}
                     </text>
                 </g>
              )}
            </svg>
            <div className="absolute top-4 right-4 bg-gray-900/80 text-white text-[8px] sm:text-[9px] font-black px-2.5 py-1.5 rounded-md uppercase tracking-widest pointer-events-none backdrop-blur-md shadow-lg border border-white/10 opacity-60 group-hover:opacity-100 transition-opacity">
                Arch {pageLabel}
            </div>
        </div>
    );
};

const PreviewPanel: React.FC<PreviewPanelProps> = ({
    config, quantity, isMobile, returnUrl, orderToken, onFinalUrlGenerated, onQuantityChange, setAlertModal, driveFolderId, isAdminMode, customBackgrounds, customTemplates, customShapes, appLogo, onRestartWizard, sheetWidth = A4_WIDTH, sheetHeight = A4_HEIGHT, bleedMm = 1, cutLinesEnabled = true, knownIconIds, compact, cartExtras = {} as CartExtras, showUsagePhotos = true
}) => {

    const [isOrdering, setIsOrdering] = useState(false);
    const [showSheet, setShowSheet] = useState(false);
    useEffect(() => {
        if (!showSheet) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowSheet(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showSheet]);
    const [orderStatus, setOrderStatus] = useState<string | null>(null);
    // The state above only reaches the button after a re-render, so two clicks within the same tick would both start an order.
    const orderingRef = useRef(false);
    const pages = calculateDynamicLayout(config, sheetWidth, sheetHeight);
    const pagesPerSet = pages.length;
    const stickerCount = pages.reduce((n, p) => n + p.length, 0);

    const finishOrdering = () => {
        orderingRef.current = false;
        setIsOrdering(false);
        setOrderStatus(null);
    };

    const [cartWarnings, setCartWarnings] = useState<CartWarning[] | null>(null);
    const [checking, setChecking] = useState(false);
    const price = estimatePrice(quantity * pagesPerSet, cartExtras);

    // Kontrola před košíkem: upozorní na ukázkový text, prázdný arch a málo ostré obrázky
    const handleCartClick = async () => {
        if (orderingRef.current || checking) return;
        if (!returnUrl || !onFinalUrlGenerated) { handleAddToCart(); return; }
        setChecking(true);
        let warnings: CartWarning[] = [];
        try { warnings = await runPreCartCheck(config, cartExtras); } catch (e) { console.warn('[Kontrola] selhala:', e); }
        if (stickerCount === 0) warnings = [{ id: 'empty', text: 'Na archu není žádná samolepka.' }];
        setChecking(false);
        if (warnings.length) { setCartWarnings(warnings); return; }
        handleAddToCart();
    };

    const handleAddToCart = async () => {
        setCartWarnings(null);
        if (orderingRef.current) return;
        if (!returnUrl || !onFinalUrlGenerated) {
            setAlertModal?.("Adresa e-shopu nebyla nalezena.");
            return;
        }
        orderingRef.current = true;
        setIsOrdering(true);
        setOrderStatus('Připravuji soubor…');
        let redirecting = false;
        try {
            const fileObj = await createStickerFile(config, quantity, orderToken || 'unknown', customBackgrounds, customShapes, appLogo, customTemplates, sheetWidth, sheetHeight, bleedMm, cutLinesEnabled);
            setOrderStatus('Odesílám na server…');
            // Validate token consistency
            if (fileObj.token !== (orderToken || 'unknown')) {
                throw new Error(`Neshoda tokenu: soubor obsahuje "${fileObj.token}", očekáváno "${orderToken}".`);
            }

            let finalRedirectUrl = returnUrl;
            try {
                const targetUrl = new URL(returnUrl);
                targetUrl.searchParams.set('order_token', fileObj.token);
                targetUrl.searchParams.set('quantity', quantity.toString());
                targetUrl.searchParams.set('quantitysheets', (quantity * pagesPerSet).toString());
                finalRedirectUrl = targetUrl.toString();

                // Validate token in return URL
                const urlToken = new URL(finalRedirectUrl).searchParams.get('order_token');
                if (urlToken !== fileObj.token) {
                    throw new Error(`Neshoda tokenu v návratové URL: "${urlToken}", očekáváno "${fileObj.token}".`);
                }

                onFinalUrlGenerated(finalRedirectUrl);
            } catch (e: any) {
                if (e.message?.includes('Neshoda tokenu')) throw e;
                console.error(e);
            }

            const drivePayload = {
                token: fileObj.token,
                createdAt: new Date().toISOString(),
                name: config.name,
                className: config.className,
                quantity: quantity,
                svgContent: fileObj.content,
                config: config,
                returnUrl: returnUrl ?? null,
                lang: getLang(),
                finalUrl: finalRedirectUrl,
                file: fileObj.blob,
                fileName: fileObj.fileName,
            };

            let driveSuccess = false;
            let previewUrl: string | undefined;
            let lastDriveError: unknown = null;
            for (let attempt = 1; attempt <= 2; attempt++) {
                try {
                    const result = await sendOrderToDrive(drivePayload);
                    previewUrl = result.previewUrl;
                    driveSuccess = true;
                    break;
                } catch (driveError) {
                    console.error(`Drive upload attempt ${attempt} failed:`, driveError);
                    lastDriveError = driveError;
                    // Sending again cannot help when the request itself is refused (no App Check token, bad file).
                    if (driveError instanceof OrderUploadError && !driveError.retryable) break;
                    if (attempt < 2) await new Promise(resolve => setTimeout(resolve, driveError instanceof OrderUploadError ? driveError.retryAfterMs : 2000));
                }
            }

            if (!driveSuccess) {
                setAlertModal?.(lastDriveError instanceof OrderUploadError ? lastDriveError.userMessage : ORDER_MESSAGES.server);
                return;
            }

            if (previewUrl) {
                try {
                    const urlWithPreview = new URL(finalRedirectUrl);
                    urlWithPreview.searchParams.set('preview_url', previewUrl);
                    finalRedirectUrl = urlWithPreview.toString();
                } catch (e) {
                    console.error(e);
                }
            }

            // Counted only now that the order is really saved. Bounded wait: the redirect below would cancel a write still in flight.
            await Promise.race([statsService.logOrder(config, knownIconIds), new Promise(resolve => setTimeout(resolve, 2000))]);

            setOrderStatus('Přesměrovávám do košíku…');
            // The order is saved and the page is about to leave: the button stays off until then, a click in
            // this last second would put the same order on Drive again. Still here well after the redirect
            // should have happened (it did not take): let the customer try again rather than leave a dead button.
            redirecting = true;
            setTimeout(() => { window.location.href = finalRedirectUrl; }, 1000);
            setTimeout(finishOrdering, 15000);
        } catch (error: any) {
             setAlertModal?.(`Chyba: ${error.message}`);
        } finally {
            if (!redirecting) finishOrdering();
        }
    };

    return (
        <div className={`rd-preview h-fit max-w-full font-sans ${compact ? 'is-compact' : ''}`}>
            {compact ? (
            <>
            <div className="rd-preview-head">
                <div>
                    <h2 className="rd-h">Tiskový arch</h2>
                    <p className="rd-sub">{stickerCount} samolepek · {pagesPerSet} {pagesPerSet === 1 ? 'arch' : pagesPerSet < 5 ? 'archy' : 'archů'} na sadu</p>
                </div>
            </div>
            <button type="button" className="rd-sheet-thumb" onClick={() => setShowSheet(true)} aria-label="Zobrazit celý tiskový arch">
                {pages[0] && (
                    <StickerSheetSvg config={config} pageLayout={pages[0]} token={orderToken} showCutLine={false} customBackgrounds={customBackgrounds} customTemplates={customTemplates} customShapes={customShapes} pageLabel={`1/${pagesPerSet}`} sheetWidth={sheetWidth} sheetHeight={sheetHeight} bleedMm={bleedMm} />
                )}
                <span className="rd-sheet-thumb-cta">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" /></svg>
                    Zobrazit celý arch
                </span>
            </button>
            {showSheet && createPortal(
                <div className="rd-sheet-modal" role="dialog" aria-modal="true" aria-label="Tiskový arch" onClick={() => setShowSheet(false)}>
                    <div className="rd-sheet-modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="rd-preview-head">
                            <div>
                                <h2 className="rd-h">Tiskový arch</h2>
                                <p className="rd-sub">Takhle samolepky rozmístíme na arch {Math.round(sheetWidth)} × {Math.round(sheetHeight)} mm · {stickerCount} samolepek</p>
                            </div>
                            <button type="button" className="rd-btn-secondary ml-auto" onClick={() => setShowSheet(false)}>Zavřít</button>
                        </div>
            <div className="space-y-8 sm:space-y-12">
                {pages.map((pageLayout, pageIdx) => (
                    <StickerSheetSvg 
                        key={`preview-page-${pageIdx}`} 
                        config={config} 
                        pageLayout={pageLayout}
                        token={orderToken} 
                        showCutLine={isAdminMode} 
                        customBackgrounds={customBackgrounds} 
                        customTemplates={customTemplates}
                        customShapes={customShapes}
                        pageLabel={`${pageIdx+1}/${pagesPerSet}`}
                        logoUrl={isAdminMode ? appLogo : undefined}
                        showCodes={isAdminMode}
                        sheetWidth={sheetWidth}
                        sheetHeight={sheetHeight}
                        bleedMm={bleedMm}
                    />
                ))}
            </div>

                    </div>
                </div>, document.body)}
            {showUsagePhotos && <UsagePhotos config={config} customBackgrounds={customBackgrounds} customShapes={customShapes} customTemplates={customTemplates} />}
            </>
            ) : (
            <>
            <div className="rd-preview-head">
                <div>
                    <h2 className="rd-h">Náhled archu</h2>
                    <p className="rd-sub">{stickerCount} samolepek · {pagesPerSet} {pagesPerSet === 1 ? 'arch' : pagesPerSet < 5 ? 'archy' : 'archů'} na sadu · {Math.round(sheetWidth)} × {Math.round(sheetHeight)} mm</p>
                </div>
                {onRestartWizard && (
                  <button onClick={onRestartWizard} title="Spustit průvodce nastavením" className="rd-btn-ghost ml-auto">Průvodce</button>
                )}
            </div>

            <div className="space-y-8 sm:space-y-12">
                {pages.map((pageLayout, pageIdx) => (
                    <StickerSheetSvg 
                        key={`preview-page-${pageIdx}`} 
                        config={config} 
                        pageLayout={pageLayout}
                        token={orderToken} 
                        showCutLine={isAdminMode} 
                        customBackgrounds={customBackgrounds} 
                        customTemplates={customTemplates}
                        customShapes={customShapes}
                        pageLabel={`${pageIdx+1}/${pagesPerSet}`}
                        logoUrl={isAdminMode ? appLogo : undefined}
                        showCodes={isAdminMode}
                        sheetWidth={sheetWidth}
                        sheetHeight={sheetHeight}
                        bleedMm={bleedMm}
                    />
                ))}
            </div>

            </>
            )}

            {typeof document !== 'undefined' && createPortal(
            <div className="rd-cartbar no-print" role="region" aria-label="Objednávka">
                <div className="rd-cartbar-inner">
                    <div className="rd-cartbar-sum">
                        <strong>{stickerCount} samolepek</strong>
                        <span>{quantity} {quantity === 1 ? 'sada' : quantity < 5 ? 'sady' : 'sad'} = {quantity * pagesPerSet} {quantity * pagesPerSet === 1 ? 'arch' : quantity * pagesPerSet < 5 ? 'archy' : 'archů'} k tisku</span>
                        <ReviewBadge />
                    </div>
                    <div className="rd-cartbar-qty">
                        <label>Počet sad</label>
                        <div className="w-36"><StepperInput value={quantity} onChange={(val) => onQuantityChange?.(Math.max(1, val))} min={1} /></div>
                    </div>
                        {isAdminMode && (
                            <button onClick={async () => {
                                try {
                                    const file = await createStickerFile(config, quantity, orderToken||'unknown', customBackgrounds, customShapes, appLogo, customTemplates, sheetWidth, sheetHeight, bleedMm, cutLinesEnabled);
                                    const formData = new FormData();
                                    formData.append('file', file.blob, file.fileName);
                                    const res = await fetch('/api/generate-pdf', { method: 'POST', headers: await getAppCheckHeader(), body: formData });
                                    if (!res.ok) throw new Error(await res.text());
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = file.fileName.replace(/\.svg$/i, '.pdf').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_');
                                    a.click();
                                    URL.revokeObjectURL(url);
                                } catch(e) { setAlertModal?.('Export PDF selhal.'); }
                            }} className="rd-btn-ghost">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0L8 8m4-4v12" /></svg>
                                <span>Tiskové PDF</span>
                            </button>
                        )}
                    <div className="rd-cartbar-cta">
                        <button onClick={handleCartClick} className="rd-btn-primary" disabled={!returnUrl || isOrdering || checking}>
                            {isOrdering ? (
                                <>
                                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                <span>{orderStatus}</span>
                                </>
                            ) : (
                                <>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                <span>Vložit do košíku</span>
                                </>
                            )}
                        </button>
                        <p className="rd-cartbar-note" role="status">{isOrdering ? 'Příprava tiskového souboru může trvat i přes minutu. Nezavírejte stránku.' : !returnUrl ? 'Editor otevřete z e-shopu, pak půjde vložit do košíku.' : price ? `Orientační cena ${formatMoney(price.total, cartExtras.priceCurrency || 'Kč')}${cartExtras.priceNote ? ` (${cartExtras.priceNote})` : ''} – ${formatMoney(price.perSheet, cartExtras.priceCurrency || 'Kč')} za arch. Přesnou cenu uvidíte v košíku.` : 'Cenu uvidíte v košíku e-shopu.'}</p>
                    </div>
                </div>
            </div>, document.body)}
            {cartWarnings && typeof document !== 'undefined' && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[90] p-4 no-print" role="dialog" aria-modal="true" aria-labelledby="precart-title">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 bg-amber-500 text-white"><h2 id="precart-title" className="text-lg font-bold">Než vložíte do košíku</h2></div>
                        <div className="p-6 space-y-4">
                            <ul className="space-y-2">
                                {cartWarnings.map(w => (
                                    <li key={w.id} className="flex gap-2 text-sm text-gray-800">
                                        <svg className="w-5 h-5 shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                        <span>{w.text}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <button onClick={() => setCartWarnings(null)} className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md">Zpět k úpravám</button>
                                <button onClick={() => handleAddToCart()} disabled={cartWarnings.some(w => w.id === 'empty')} className="flex-1 py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-md border-2 border-gray-200 disabled:opacity-40">Přesto vložit do košíku</button>
                            </div>
                        </div>
                    </div>
                </div>, document.body)}
        </div>
    );
};

export default PreviewPanel;
